import type { BlockDef } from '@cogo/types-v2'

/**
 * Extract all #symbol references from a block definition.
 *
 * Walks ui, events, flow, state, data (skipping _editor keys).
 * Returns deduplicated, alphabetically sorted array.
 */
export function extractSymbols(block: BlockDef): string[] {
  const symbols = new Set<string>()

  // Bare symbol: #identifier.path
  const barePattern = /#[a-zA-Z_][a-zA-Z0-9_.]*/g
  // Expression-wrapped: ${#identifier.path...}
  const exprPattern = /\$\{(#[a-zA-Z_][a-zA-Z0-9_.]*)[^}]*\}/g

  function walk(value: unknown, key?: string): void {
    if (key === '_editor') return

    if (typeof value === 'string') {
      // Extract from expressions first (to get the #symbol part inside ${})
      let match: RegExpExecArray | null
      const exprRe = new RegExp(exprPattern.source, 'g')
      while ((match = exprRe.exec(value)) !== null) {
        symbols.add(match[1])
      }
      // Then extract bare symbols not inside ${}
      const bareRe = new RegExp(barePattern.source, 'g')
      while ((match = bareRe.exec(value)) !== null) {
        // Check if this match is inside a ${} expression
        const idx = match.index
        const before = value.substring(0, idx)
        const lastOpen = before.lastIndexOf('${')
        const lastClose = before.lastIndexOf('}')
        if (lastOpen !== -1 && (lastClose === -1 || lastClose < lastOpen)) {
          // Inside an expression — already captured by exprPattern
          continue
        }
        symbols.add(match[0])
      }
      return
    }

    if (Array.isArray(value)) {
      for (const item of value) {
        walk(item)
      }
      return
    }

    if (value !== null && typeof value === 'object') {
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        walk(v, k)
      }
    }
  }

  // Walk all sections except _editor at top level
  walk(block.ui)
  walk(block.events)
  walk(block.flow)
  walk(block.state)
  walk(block.data)

  return Array.from(symbols).sort()
}
